import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import {
  calculateCheckoutBreakdown,
  PAYMENT_PROCESSING_FEE_LABEL,
  SALES_TAX_FEE_LABEL,
} from '../utils/feeCalculation';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

export interface PaymentBreakdownLine {
  label: string;
  servicePrice: number;
  platformFeePercent: number;
  /** e.g. "per visit", "per month", "at first service" */
  chargeNote?: string;
  /** Sales tax % (provider-configured). Always shown; use 0 for $0.00. */
  salesTaxPercent?: number | null;
  salesTaxState?: string | null;
}

interface PaymentBreakdownTableProps {
  lines: PaymentBreakdownLine[];
  compact?: boolean;
  /** Override footer caption (e.g. provider Bid Details) */
  footerNote?: string;
}

const DEFAULT_FOOTER =
  'Your card will be charged automatically per your service agreement. Sales tax (if any) is collected for and remitted by your provider. Payment processing fees cover secure electronic payment costs and are not part of the provider\'s service price.';

const PaymentBreakdownTable: React.FC<PaymentBreakdownTableProps> = ({
  lines,
  compact,
  footerNote = DEFAULT_FOOTER,
}) => {
  if (lines.length === 0) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {lines.map((line, index) => {
        const breakdown = calculateCheckoutBreakdown(line.servicePrice, line.platformFeePercent, {
          salesTaxPercent: line.salesTaxPercent,
          salesTaxState: line.salesTaxState,
        });
        const taxLabel = breakdown.salesTaxState
          ? `${SALES_TAX_FEE_LABEL} (${breakdown.salesTaxState})`
          : SALES_TAX_FEE_LABEL;
        return (
          <Box
            key={`${line.label}-${index}`}
            sx={{
              mb: 2,
              p: compact ? 1.5 : 2,
              backgroundColor: 'white',
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.200',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {line.label}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Service price
              </Typography>
              <Typography variant="body2">{formatCurrency(breakdown.servicePrice)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {taxLabel}
              </Typography>
              <Typography variant="body2">{formatCurrency(breakdown.salesTaxAmount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {PAYMENT_PROCESSING_FEE_LABEL}
              </Typography>
              <Typography variant="body2">{formatCurrency(breakdown.processingFee)}</Typography>
            </Box>
            <Divider sx={{ my: 0.75 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" fontWeight={600}>
                Total charged{line.chargeNote ? ` (${line.chargeNote})` : ''}
              </Typography>
              <Typography variant="body2" fontWeight={700} color="primary.main">
                {formatCurrency(breakdown.totalCharge)}
              </Typography>
            </Box>
          </Box>
        );
      })}
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {footerNote}
      </Typography>
    </Box>
  );
};

export default PaymentBreakdownTable;
